// Kampf am Ufer (§6): Kniffe (K6) und die Abstecher, Sprünge und Rollen, die sie auslösen.
// Aufgeteilt aus battle.ts.
import {
  getAbility,
  getEnemy,
} from '../../content/index.ts';
import type {
  TelegraphShape,
  Unit,
} from '../types.ts';
import type { Battle } from './core.ts';
import { kniffArt, launch } from './shots.ts';
import {
  EPS,
  param,
  optParam,
  unitById,
  dist,
  gap,
  round,
  emit,
  targetable,
  enemiesOf,
  nearest,
  setStatus,
  moveToward,
  clampToField,
  dealDamage,
  shove,
  heal,
  applyHeal,
  addPoison,
  hitDamage,
  attack,
  splash,
  RECENT_SECONDS,
  recentDamage,
  hardestHit,
  weakestOf,
  lowestHpEnemy,
  mostHurtFriend,
  placeNextTo,
  provoke,
} from './core.ts';
import { markWeakSpot, startBall, startBeckon, startMulde, startWindup, telegraphed } from './orders.ts';

/** K6: Mut voll, Kniff. Gibt false zurück, wenn gerade kein Ziel da ist. */
export function fireKniff(b: Battle, u: Unit): boolean {
  if (!u.kniff) return false;
  // K48: Große Angriffe der Sturmwesen werden erst angesagt
  if (telegraphed(b, u, u.kniff)) return !u.windup && startWindup(b, u, u.kniff);
  // K23 Verpeilt (Fabi, Runde 7): vergisst jedes zweite Mal seinen Kniff; kurz blitzt ein Fragezeichen auf
  const dizzy = u.traits.includes('verpeilt');
  if (dizzy && u.kniffTries % 2 === 1) {
    u.kniffTries += 1;
    u.mut = 0;
    u.confusedUntil = b.t + 1;
    setStatus(b, u, 'confused', u.confusedUntil, 'trait:verpeilt');
    return true;
  }
  // Fabi (Phase Kern): Was der Kniff an Zuständen und Heilungen auslöst, trägt seinen Namen
  const before = b.via;
  b.via = `kniff:${u.kniff}`;
  let ok = false;
  try {
    ok = useKniff(b, u);
  } finally {
    b.via = before;
  }
  if (ok && dizzy) u.kniffTries += 1;
  return ok;
}

export function useKniff(b: Battle, u: Unit): boolean {
  const k = u.kniff;
  if (!k) return false;
  const current = unitById(b, u.targetUid);
  let target = current && targetable(b, current) && current.side !== u.side ? current : nearest(u, enemiesOf(b, u));

  // S5 Eisvogel: Bei vollem Mut erst eine Schwachstelle ausmachen (F35 Späher: mehrere), dann der Kniff
  if (u.passive === 'schwachstelle') {
    const marks = Math.max(1, Math.round(optParam(u, 'schwachstelle', 'marks') || 1));
    for (let i = 0; i < marks; i++) {
      const foes = b.units.filter((o) => o.side !== u.side && o.alive && o.kind !== 'tree');
      if (foes.length === 0) break;
      const m = foes[Math.floor(b.rng.next() * foes.length)]!;
      m.marked = true;
      m.markedBy = u.uid;
      emit(b, { t: round(b.t), type: 'mark', by: u.uid, to: m.uid });
    }
  }

  let kTarget: Unit | undefined = target;
  /** K46: der Ort, auf den ein Kniff mit Ortsziel geht */
  let kAt: { x: number; y: number } | null = null;
  // X3: Die Regel hinter dem Kniff steht in abilities.json („mechanic“), die Zahlen ebenda oder im Profil des Wesens
  const mechanic = getAbility(k).mechanic;
  switch (mechanic) {
    case 'mulde':
      // K50 Bau graben (neu): unter der Erde zum Ort, dort graben, der Boden sackt ab
      kAt = startMulde(b, u, k);
      setStatus(b, u, 'burrowed', b.t);
      kTarget = undefined;
      break;
    case 'ball': {
      // K50 Stachelball (neu): rollt zum Ort und kreiselt dort
      kAt = startBall(b, u, k);
      if (!kAt) return false;
      kTarget = undefined;
      break;
    }
    case 'beckon': {
      // K50 Wunden auswaschen (neu): winkt einen Freund heran und wäscht ihn aus
      const f = startBeckon(b, u, k);
      if (!f) return false;
      kTarget = f;
      break;
    }
    case 'weakspot': {
      // K51 Schwachstelle als Kniff: die Zielscheibe bleibt, bis ein Kniff sie trifft
      const m = markWeakSpot(b, u);
      if (!m) return false;
      kTarget = m;
      break;
    }
    case 'guard': {
      // Bau graben / Bollwerk: er und die Nachbarn im Radius nehmen weniger Schaden
      const until = b.t + param(u, k, 'duration');
      for (const o of b.units) {
        if (o.side !== u.side || !o.alive || o.kind === 'tree' || dist(o, u) > param(u, k, 'radius') + EPS) continue;
        guard(b, u, o, param(u, k, 'damageReduction'), until);
      }
      kTarget = undefined;
      break;
    }
    case 'guardian': {
      // S10 Leibwache (Fabi, Phase Kern): Wird sein Schützling angegriffen, eilt er hin; ohne Schützling an Ort und Stelle
      const ward = unitById(b, u.wardUid);
      if (ward && ward.alive) {
        if (!attackersOf(b, ward).length) return false; // er wartet, bis jemand den Schützling angreift
        if (gap(u, ward) > GUARD_REACH) {
          u.guardRun = { wardUid: ward.uid, until: b.t + param(u, k, 'dashSeconds') };
          setStatus(b, u, 'guarding', u.guardRun.until);
          kTarget = ward;
          break;
        }
      }
      standGuard(b, u, k, ward && ward.alive ? ward : undefined);
      kTarget = ward && ward.alive ? ward : undefined;
      break;
    }
    case 'taunt': {
      tauntAround(b, u, param(u, k, 'radius'), param(u, k, 'attacks'));
      // R27 Igelburg: dabei selbst eingerollt, nimmt kaum Schaden
      if (optParam(u, k, 'selfReduction') > 0) guard(b, u, u, optParam(u, k, 'selfReduction'), b.t + param(u, k, 'duration'));
      kTarget = undefined;
      break;
    }
    case 'buff': {
      // R27 Nussgeschenk: denen, denen es am schlechtesten geht (Vorratskammer: mehreren)
      const friends = b.units.filter((o) => o.side === u.side && o.alive && o.kind !== 'tree');
      // Vorratskammer (Fabi): an die, die in den letzten Sekunden am meisten eingesteckt haben, nicht immer an den Schwächsten
      const order = optParam(u, k, 'byRecent') > 0 ? hardestHit(b, friends) : [...friends].sort((a, c) => a.hp / a.maxHp - c.hp / c.maxHp || a.uid - c.uid);
      const mates = order.slice(0, Math.max(1, Math.round(optParam(u, k, 'count') || 1)));
      if (!mates.length) return false;
      const until = b.t + param(u, k, 'duration');
      for (const mate of mates) {
        empower(b, mate, 1 + param(u, k, 'damageBoost'), until);
        guard(b, u, mate, param(u, k, 'reduction'), until);
        heal(b, u, mate, param(u, k, 'heal'));
        // Harte Schale: Die Nuss schützt zusätzlich mit einem Schild
        if (optParam(u, k, 'shield') > 0) {
          mate.shield = Math.max(mate.shield, optParam(u, k, 'shield'));
          mate.shieldSource = u.uid;
          setStatus(b, mate, 'shielded', b.t);
        }
      }
      // Festmahl: Alle bekommen ein bisschen ab
      if (optParam(u, k, 'allHeal') > 0) for (const o of friends) heal(b, u, o, optParam(u, k, 'allHeal'));
      kTarget = mates[0];
      break;
    }
    case 'rage': {
      // R27 Raserei: mehr Schaden, weniger Schaden nehmen
      const until = b.t + param(u, k, 'duration');
      empower(b, u, 1 + param(u, k, 'damageBoost'), until);
      guard(b, u, u, param(u, k, 'reduction'), until);
      kTarget = undefined;
      break;
    }
    case 'dive': {
      // R27 Stoßtauchen (Fabi): 1) markieren (oben), 2) losfliegen, 3) zustoßen und auslösen, 4) zurück.
      // Der Stoß kommt nach einer kurzen Anflugzeit (updateDive); die Markierung ist so lange für ihn reserviert.
      const marked = [...b.events].reverse().find((e) => e.type === 'mark' && e.by === u.uid && e.t >= round(b.t) - EPS);
      const victim = (marked && marked.type === 'mark' ? unitById(b, marked.to) : undefined) ?? lowestHpEnemy(b, u);
      if (!victim || !victim.alive) return false;
      if (victim.marked && victim.markedBy === u.uid) victim.markReservedBy = u.uid;
      u.dive = { targetUid: victim.uid, at: b.t + param(u, k, 'windup') };
      u.mut = 0;
      u.kniffUses += 1;
      return true;
    }
    case 'burrow': {
      // R27 Erdüberraschung (Fabi): kurz eingegraben und unangreifbar. Spieltest Phase 4: Wer ihn im Blick hatte,
      // verliert ihn nicht, sondern wartet, bis er wieder auftaucht; sonst fehlt die Frontlinie
      u.burrowUntil = b.t + param(u, k, 'burrowSeconds');
      setStatus(b, u, 'burrowed', u.burrowUntil);
      kTarget = undefined;
      break;
    }
    case 'volley': {
      // R27 Kieselwurf: mehrere Gegner, die Kiesel zerschellen
      const foes = b.units
        .filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree')
        .sort((a, c) => gap(u, a) - gap(u, c) || a.uid - c.uid)
        .slice(0, Math.max(1, Math.round(param(u, k, 'targets'))));
      if (!foes.length) return false;
      for (const o of foes) {
        const raw = hitDamage(b, u, param(u, k, 'damageFactor'));
        dealDamage(b, u, o, raw, 'kniff');
        splash(b, u, o, raw * param(u, k, 'splashShare'), param(u, k, 'splashRadius'), `kniff:${k}`);
      }
      kTarget = foes[0];
      break;
    }
    case 'leap': {
      const victim = lowestHpEnemy(b, u);
      if (!victim) return false;
      u.onTree = false; // S5: Für den Kniff verlässt es den Baum und ist angreifbar
      placeNextTo(b, u, victim);
      u.leap = { targetUid: victim.uid, hitsLeft: param(u, k, 'hits'), nextHitAt: b.t + param(u, k, 'hitInterval') };
      // F35 Wirbelwind (Fabi): die drei schwächsten Gegner reihum, jeder zweimal: 1 → 2 → 3 → 1 → 2 → 3
      if (optParam(u, k, 'spread') > 0) {
        const pool = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree');
        const cycle: number[] = [];
        while (cycle.length < 3) {
          const next = weakestOf(u, pool.filter((o) => !cycle.includes(o.uid)));
          if (!next) break;
          cycle.push(next.uid);
        }
        u.leap.cycle = cycle;
      }
      emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'leap' });
      kTarget = victim;
      break;
    }
    case 'strike': {
      // F35 Tiefe Wunde (Fabi, Runde 7): Der Sturzflug zielt immer auf einen Gegner mit Schwachstelle
      if (optParam(u, k, 'seekMark') > 0) {
        const marked = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.marked);
        const mine = marked.filter((o) => o.markedBy === u.uid);
        const pickFrom = mine.length ? mine : marked;
        if (pickFrom.length) target = nearest(u, pickFrom);
      }
      if (!target) return false;
      // K33 (Phase Kern): Ein Hieb (Messerhieb, Blattschwert) braucht ein Ziel in Reichweite, sonst wartet er
      if (optParam(u, k, 'melee') > 0 && gap(u, target) > u.range + EPS) return false;
      // Fabi (Spieltest Phase 4): fester Schaden plus Prozent vom Leben des Ziels; gegen Zähe trifft er härter
      dealDamage(b, u, target, hitDamage(b, u, param(u, k, 'damageFactor')) + target.maxHp * optParam(u, k, 'maxHpShare'), 'kniff');
      kTarget = target;
      break;
    }
    case 'poison':
      if (!target) return false;
      addPoison(b, u, target, param(u, k, 'stacks'));
      break;
    case 'heal': {
      const patient = mostHurtFriend(b, u, true);
      if (!patient) return false;
      // F35 Heiler (Fabi): Wunden auswaschen heilt über ein paar Sekunden, dafür stärker
      const over = optParam(u, k, 'overSeconds');
      if (over > 0) {
        const perTick = param(u, k, 'heal') / (over + 1);
        applyHeal(b, u, patient, perTick);
        patient.hots.push({ perTick, ticksLeft: over, nextTick: b.t + 1, source: u.uid });
      } else heal(b, u, patient, param(u, k, 'heal'));
      kTarget = patient;
      break;
    }
    case 'shield': {
      const friends = b.units.filter((o) => o.side === u.side && o.alive && o.kind !== 'tree');
      // F35 Schutzpatron (Fabi): ein Schild für den, der in den letzten Sekunden am meisten eingesteckt hat,
      // so stark wie ein Teil davon, mindestens ein Grundschild
      const fromRecent = optParam(u, k, 'fromRecent');
      if (fromRecent > 0) {
        // Fabi (Runde 7): Schutzpatron schaut auf die letzten 2,5 s [ZAHL?]
        const seconds = optParam(u, k, 'recentSeconds') || RECENT_SECONDS;
        const o = hardestHit(b, friends, seconds)[0];
        if (!o) return false;
        o.shield = Math.max(o.shield, param(u, k, 'minAmount'), fromRecent * recentDamage(b, o, seconds));
        o.shieldSource = u.uid;
        setStatus(b, o, 'shielded', b.t);
        kTarget = o;
        break;
      }
      // F35 Schutzschild: an die, denen es am schlechtesten geht; wer schon einen hat, bekommt ihn aufgefüllt
      const mates = [...friends].sort((a, c) => a.hp / a.maxHp - c.hp / c.maxHp || a.uid - c.uid).slice(0, Math.max(1, Math.round(param(u, k, 'count'))));
      for (const o of mates) {
        o.shield = Math.max(o.shield, param(u, k, 'amount'));
        o.shieldSource = u.uid;
        setStatus(b, o, 'shielded', b.t);
      }
      kTarget = mates[0];
      break;
    }
    case 'curl':
      // F35 Stachelkugel: eingerollt, angreifbar, die Angreifer bleiben dran und stechen sich
      u.spikyUntil = b.t + param(u, k, 'duration');
      setStatus(b, u, 'spiky', u.spikyUntil);
      kTarget = undefined;
      break;
    case 'spin': {
      // F35 Stachelball: rollt auf den Gegner mit dem meisten Leben zu und kreiselt dort
      const victim = mostHpEnemy(b, u);
      if (!victim) return false;
      u.spin = { targetUid: victim.uid, started: -1, until: Infinity, nextTick: 0 };
      u.rushUntil = b.t + 4;
      setStatus(b, u, 'spinning', b.t);
      kTarget = victim;
      break;
    }
    case 'slow': {
      if (!target) return false;
      // K47: Der Schlammspritzer fliegt als Geschoss und macht langsamer, wen er trifft
      const art = b.setup.projectiles ? kniffArt(b, k) : null;
      if (art) {
        launch(b, u, target, { kind: 'slow', slow: param(u, k, 'slow'), duration: param(u, k, 'duration'), via: `kniff:${k}` }, art);
        break;
      }
      target.slowUntil = b.t + param(u, k, 'duration');
      target.slowFactor = param(u, k, 'slow');
      setStatus(b, target, 'slowed', target.slowUntil);
      break;
    }
    case 'ram': {
      if (!target) return false;
      dealDamage(b, u, target, hitDamage(b, u, param(u, k, 'damageFactor')), 'kniff');
      if (target.alive && target.kind !== 'tree' && !target.onTree) {
        const d = Math.max(dist(u, target), EPS);
        // Spieltest Phase 4 (Fabi): Leichte Tiere fliegen weiter; ein Stoß über den Rand kann einen aus dem Kampf werfen
        const push = param(u, k, 'knockback') * (b.rules.edge.pushRadius / target.radius) ** 2;
        shove(b, target, ((target.x - u.x) / d) * push, ((target.y - u.y) / d) * push);
        if (target.alive) emit(b, { t: round(b.t), type: 'move', unit: target.uid, x: round(target.x), y: round(target.y), reason: 'knockback' });
      }
      break;
    }
    case 'pounce': {
      // G26 Nieselchen (Fabi, Phase Kern): springt zu dem Freund in seiner Nähe, der am meisten Schaden gemacht hat,
      // und schlägt zweimal schnell mit dem Blattschwert zu; danach versteckt es sich wieder (updateHider)
      const near = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree' && dist(o, u) <= param(u, k, 'radius') + EPS);
      const victim = [...near].sort((a, c) => c.stats.damage - a.stats.damage || dist(a, u) - dist(c, u) || a.uid - c.uid)[0] ?? nearest(u, enemiesOf(b, u).filter((o) => o.kind !== 'tree'));
      if (!victim) return false;
      if (gap(u, victim) > u.hide!.meleeRange - EPS) {
        placeNextTo(b, u, victim);
        emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'leap' });
      }
      const left = Math.round(param(u, k, 'hits')) - 1;
      u.pounce = { targetUid: victim.uid, hitsLeft: left, nextHitAt: b.t + param(u, k, 'hitInterval') };
      dealDamage(b, u, victim, hitDamage(b, u, param(u, k, 'damageFactor')), 'kniff');
      provoke(b, u, victim);
      if (u.pounce && (left <= 0 || !victim.alive)) u.pounce.lingerUntil = b.t + param(u, k, 'lingerSeconds');
      kTarget = victim;
      break;
    }
    case 'raid': {
      // G18: noch ein Abstecher in die hintere Reihe
      const back = startRaid(b, u);
      if (!back) return false;
      kTarget = back;
      break;
    }
    case 'blind': {
      // G22 Auf die Augen (Fabi, Runde 7): Dreck in die Augen der Freunde rund um das Ziel; ihre nächsten Angriffe sind schwächer
      const victim = nearest(u, enemiesOf(b, u).filter((o) => o.kind !== 'tree'));
      if (!victim || gap(u, victim) > u.range * 1.5) return false;
      const around = b.units
        .filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree' && dist(o, victim) <= param(u, k, 'radius') + EPS)
        .sort((a, c) => dist(a, victim) - dist(c, victim))
        .slice(0, Math.max(1, Math.round(param(u, k, 'targets'))));
      for (const o of around) weaken(b, o, 'blinded', param(u, k, 'attacks'), param(u, k, 'factor'));
      kTarget = victim;
      break;
    }
    case 'fell': {
      // S13 Biber, Baum fällen (Erste Fassung): ohne Ansage (Übung, Tests) kracht der Stamm gleich
      if (!target) return false;
      const shape = fellShape(u, k, target);
      if (!shape) return false;
      fellHit(b, u, k, shape);
      kTarget = target;
      break;
    }
    case 'feint': {
      // S13 Fuchs, Finte (Erste Fassung): Das Sturmwesen vor ihm schlägt ins Leere und trifft seinen Nachbarn; danach ist
      // es benommen, und der Fuchs landet selbst einen Treffer
      const foe = target;
      if (!foe || foe.kind === 'tree' || gap(u, foe) > u.range + 2 + EPS) return false;
      const mate = nearest(foe, b.units.filter((o) => o.side === foe.side && o !== foe && targetable(b, o) && o.kind !== 'tree'));
      if (mate) dealDamage(b, foe, mate, foe.damage * param(u, k, 'factor'), 'kniff');
      daze(b, foe, param(u, k, 'dazeSeconds'), param(u, k, 'slow'), param(u, k, 'attacks'), param(u, k, 'weak'));
      if (foe.alive) dealDamage(b, u, foe, hitDamage(b, u, param(u, k, 'damageFactor')), 'kniff');
      kTarget = foe;
      break;
    }
    case 'decoy': {
      // S13 Fuchs, Ablenkung (Erste Fassung): zu dem Freund, dem es am schlechtesten geht, „Hierher!“, und er weicht aus
      const ward = mostHurtFriend(b, u, true);
      if (ward && ward !== u && gap(u, ward) > GUARD_REACH) {
        placeNextTo(b, u, ward);
        emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'leap' });
      }
      tauntAround(b, u, param(u, k, 'radius'), param(u, k, 'attacks'), ward && ward !== u ? attackersOf(b, ward) : []);
      guard(b, u, u, param(u, k, 'selfReduction'), b.t + param(u, k, 'duration'));
      kTarget = ward && ward !== u ? ward : undefined;
      break;
    }
    case 'skip': {
      // S13 Otter, Flitschstein (Erste Fassung): Der Stein hüpft von Sturmwesen zu Sturmwesen und trifft jedes, auf dem er aufsetzt
      if (!target) return false;
      const hit: Unit[] = [];
      let at: Unit | undefined = target;
      while (at && hit.length < Math.max(1, Math.round(param(u, k, 'targets')))) {
        hit.push(at);
        const from: Unit = at;
        at = nearest(from, b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree' && !hit.includes(o) && dist(o, from) <= param(u, k, 'hop') + EPS));
      }
      for (const o of hit) dealDamage(b, u, o, hitDamage(b, u, param(u, k, 'damageFactor')), 'kniff');
      kTarget = target;
      break;
    }
    case 'rumble': {
      // G24 Bedrohliches Grollen (Fabi, Runde 7): Wer in der Nähe ist, schreckt zurück und greift danach zaghaft an
      for (const o of b.units) {
        if (o.side === u.side || !targetable(b, o) || o.kind === 'tree' || dist(o, u) > param(u, k, 'radius') + EPS) continue;
        weaken(b, o, 'timid', param(u, k, 'attacks'), param(u, k, 'factor'));
        const d = Math.max(dist(u, o), EPS);
        const push = param(u, k, 'push') * (b.rules.edge.pushRadius / o.radius) ** 2;
        shove(b, o, ((o.x - u.x) / d) * push, ((o.y - u.y) / d) * push);
        if (o.alive) emit(b, { t: round(b.t), type: 'move', unit: o.uid, x: round(o.x), y: round(o.y), reason: 'knockback' });
      }
      kTarget = undefined;
      break;
    }
    case 'shout': {
      // S8 Gezeter (Runde 7): Sturmwesen in der Nähe erschrecken und werden langsamer, die Elster trumpft auf
      const until = b.t + param(u, k, 'duration');
      for (const o of b.units) {
        if (o.side === u.side || !targetable(b, o) || o.kind === 'tree' || dist(o, u) > param(u, k, 'radius') + EPS) continue;
        o.slowUntil = until;
        o.slowFactor = param(u, k, 'slow');
        setStatus(b, o, 'slowed', until);
        // Runde 8 (Fabi): Wer die Elster gerade angreift, weicht erschrocken zurück, bis er sie nicht mehr erreicht,
        // und sucht sich danach vielleicht ein neues Ziel. Nach Runde 8 (Fabi): Das Zurückweichen war kaum zu sehen;
        // der Abstand ist größer [ZAHL?], und die Darstellung zeigt es mit Ausrufezeichen und Spur („scared“)
        if (o.targetUid === u.uid) {
          const d = dist(o, u) || 1;
          const back = Math.max(0, o.range + optParam(u, k, 'pushMargin') - gap(o, u));
          if (back > EPS) {
            o.x += ((o.x - u.x) / d) * back;
            o.y += ((o.y - u.y) / d) * back;
            clampToField(b, o);
            emit(b, { t: round(b.t), type: 'move', unit: o.uid, x: round(o.x), y: round(o.y), reason: 'scared' });
          }
          o.targetUid = null;
          o.engagedUid = null;
        }
      }
      empower(b, u, 1 + param(u, k, 'damageBoost'), until);
      kTarget = undefined;
      break;
    }
    case 'snatch': {
      // S8 Glitzerklau (Runde 7): zum Sturmwesen mit Item, sonst zu dem, das am meisten Schaden macht; ein Item
      // schnappt sie sich, und das Sturmwesen ist eine Weile abgelenkt
      const foes = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree');
      const shiny = foes.filter((o) => o.items.length > 0);
      const victim = nearest(u, shiny) ?? [...foes].sort((a, c) => c.damage / c.attackInterval - a.damage / a.attackInterval || a.uid - c.uid)[0];
      if (!victim) return false;
      placeNextTo(b, u, victim);
      emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'leap' });
      for (let i = 0; i < Math.round(param(u, k, 'hits')) && victim.alive; i++) dealDamage(b, u, victim, hitDamage(b, u, param(u, k, 'damageFactor')), 'kniff');
      if (victim.items.length) u.loot.push(victim.items.shift()!);
      if (victim.alive) weaken(b, victim, 'distracted', param(u, k, 'attacks'), param(u, k, 'factor'));
      // K36 (Fabi, Phase Kern): Wer mit einem Sprung direkt vor einem Gegner landet und ihn trifft, zieht ihn an
      provoke(b, u, victim);
      kTarget = victim;
      break;
    }
    case 'toxicskin': {
      // S8 Hautgift (Runde 7): weniger Schaden nehmen, Angreifer vergiften
      const until = b.t + param(u, k, 'duration');
      u.toxicUntil = until;
      guard(b, u, u, param(u, k, 'reduction'), until);
      setStatus(b, u, 'toxic', until);
      kTarget = undefined;
      break;
    }
    case 'spray': {
      // S8 Giftspritzer (Runde 7): Gift auf mehrere Sturmwesen in Reichweite
      const foes = b.units
        .filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree' && gap(u, o) <= u.range + EPS)
        .sort((a, c) => gap(u, a) - gap(u, c) || a.uid - c.uid)
        .slice(0, Math.max(1, Math.round(param(u, k, 'targets'))));
      if (!foes.length) return false;
      for (const o of foes) addPoison(b, u, o, Math.round(param(u, k, 'stacks')));
      kTarget = foes[0];
      break;
    }
    case 'reset':
      // G25 Doppelsprung (Fabi, Runde 7): Der nächste Angriff kommt sofort, sobald er gelandet ist
      u.attackCooldown = 0;
      kTarget = undefined;
      break;
    case 'haste':
      u.hasteUntil = b.t + param(u, k, 'duration');
      u.hasteFactor = param(u, k, 'attackSpeedFactor');
      setStatus(b, u, 'hasted', u.hasteUntil);
      kTarget = undefined;
      break;
    default:
      throw new Error(`Kniff ohne Umsetzung: ${k}`);
  }
  u.mut = 0;
  u.kniffUses += 1;
  emit(b, { t: round(b.t), type: 'kniff', unit: u.uid, kniff: k, target: kTarget?.uid ?? null, ...(kAt ? { x: round(kAt.x), y: round(kAt.y) } : {}) });
  return true;
}

/** S13 (Erste Fassung): benommen, also eine Weile langsamer und die nächsten Angriffe zaghaft */
export function daze(b: Battle, o: Unit, seconds: number, slow: number, attacks: number, factor: number): void {
  if (!o.alive || o.kind === 'tree') return;
  // slowFactor: so viel langsamer (0,5 heißt halb so schnell)
  o.slowFactor = o.slowUntil > b.t ? Math.max(o.slowFactor, slow) : slow;
  o.slowUntil = Math.max(o.slowUntil, b.t + seconds);
  setStatus(b, o, 'slowed', o.slowUntil);
  weaken(b, o, 'timid', attacks, factor);
}

/** S13 Baum fällen: der Streifen, in den der Stamm fällt, vom Biber zum Ziel; null, wenn das Ziel zu weit weg ist */
export function fellShape(u: Unit, k: string, target: Unit): Extract<TelegraphShape, { kind: 'stripe' }> | null {
  const len = param(u, k, 'stripeLength');
  if (gap(u, target) > len - u.radius) return null;
  const d = Math.max(EPS, dist(u, target));
  return { kind: 'stripe', x: round(u.x), y: round(u.y), dx: round((target.x - u.x) / d), dy: round((target.y - u.y) / d), length: len + u.radius, width: param(u, k, 'stripeWidth') };
}

/** S13 Baum fällen: Wer im Streifen steht, wird getroffen und ist benommen; gibt zurück, ob jemand getroffen wurde */
export function fellHit(b: Battle, u: Unit, k: string, shape: Extract<TelegraphShape, { kind: 'stripe' }>): boolean {
  const hit: Unit[] = [];
  for (const o of b.units) {
    if (o.side === u.side || !o.alive || o.kind === 'tree' || !targetable(b, o)) continue;
    const rx = o.x - shape.x;
    const ry = o.y - shape.y;
    const along = rx * shape.dx + ry * shape.dy;
    const across = Math.abs(rx * shape.dy - ry * shape.dx);
    if (along >= -o.radius && along <= shape.length + o.radius && across <= shape.width / 2 + o.radius) hit.push(o);
  }
  for (const o of hit) {
    dealDamage(b, u, o, hitDamage(b, u, param(u, k, 'damageFactor')), 'kniff');
    daze(b, o, param(u, k, 'dazeSeconds'), param(u, k, 'slow'), param(u, k, 'attacks'), param(u, k, 'factor'));
  }
  return hit.length > 0;
}

/** Weniger Schaden nehmen; der stärkere Schutz gewinnt, verhinderter Schaden zählt für den, der schützt */
/** G22, G24: Die nächsten Angriffe machen weniger Schaden */
export function weaken(b: Battle, o: Unit, kind: 'blinded' | 'timid' | 'distracted', attacks: number, factor: number): void {
  o.weakAttacks = Math.max(o.weakAttacks, Math.round(attacks));
  o.weakFactor = Math.min(o.weakKind ? o.weakFactor : 1, factor);
  o.weakKind = kind;
  setStatus(b, o, kind, b.t + 4);
}

export function guard(b: Battle, src: Unit, o: Unit, reduction: number, until: number): void {
  if (o.guardUntil > b.t && o.guardReduction > reduction + EPS) return;
  o.guardUntil = until;
  o.guardReduction = Math.min(0.9, reduction);
  o.guardSource = src.uid;
  setStatus(b, o, 'guarded', until);
}

export function empower(b: Battle, o: Unit, factor: number, until: number): void {
  o.empowerFactor = o.empowerUntil > b.t ? Math.max(o.empowerFactor, factor) : factor;
  o.empowerUntil = Math.max(o.empowerUntil, until);
  setStatus(b, o, 'empowered', until);
}

/** S10 Leibwache: so nah muss er am Schützling sein, damit er nicht erst hinläuft */
export const GUARD_REACH = 2;

/** S10: Wer greift diesen Freund gerade an (zielt auf ihn und erreicht ihn)? */
export function attackersOf(b: Battle, ward: Unit): Unit[] {
  return b.units.filter((e) => e.side !== ward.side && e.alive && e.kind !== 'tree' && (e.targetUid === ward.uid || e.raid?.targetUid === ward.uid) && gap(e, ward) <= e.range + 1 + EPS);
}

/**
 * S10 Leibwache: Stacheln aufstellen und „Komm doch her!“ rufen. Wer in der Nähe steht und wer den Schützling angreift,
 * greift eine Weile nur ihn an; er selbst nimmt weniger Schaden.
 */
export function standGuard(b: Battle, u: Unit, k: string, ward?: Unit): void {
  const attackers = ward ? attackersOf(b, ward) : [];
  tauntAround(b, u, param(u, k, 'radius'), param(u, k, 'attacks'), attackers);
  guard(b, u, u, param(u, k, 'selfReduction'), b.t + param(u, k, 'duration'));
}

/** S10: unterwegs zum Schützling, schnell wie ein huschender Igel; angekommen (oder nach der Höchstdauer) hält er Wache */
export function updateGuardRun(b: Battle, u: Unit): boolean {
  const g = u.guardRun;
  if (!g || !u.kniff) return false;
  const ward = unitById(b, g.wardUid);
  if (!ward || !ward.alive || gap(u, ward) <= GUARD_REACH || b.t + EPS >= g.until) {
    u.guardRun = null;
    const before = b.via;
    b.via = `kniff:${u.kniff}`;
    try {
      standGuard(b, u, u.kniff, ward && ward.alive ? ward : undefined);
    } finally {
      b.via = before;
    }
    return true;
  }
  moveToward(b, u, ward.x, ward.y, param(u, u.kniff, 'dashSpeed'));
  return true;
}

/** R27 Erdüberraschung: auftauchen unter dem Gegner mit dem wenigsten Leben in der Nähe, sonst beim nächsten */
/** Tanken: Gegner in der Nähe greifen eine Weile nur ihn an (Fanatische nicht, K23) */
export function tauntAround(b: Battle, u: Unit, radius: number, attacks: number, also: Unit[] = []): void {
  for (const o of b.units) {
    if (o.side === u.side || !o.alive || (dist(o, u) > radius + EPS && !also.includes(o)) || o.traits.includes('fanatisch')) continue;
    o.tauntBy = u.uid;
    o.tauntAttacks = attacks;
    o.targetUid = u.uid;
    setStatus(b, o, 'taunted', b.t);
  }
}

export function emerge(b: Battle, u: Unit): void {
  const k = u.kniff!;
  const near = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree' && gap(u, o) <= param(u, k, 'searchRadius') + EPS);
  const victim = weakestOf(u, near) ?? nearest(u, enemiesOf(b, u));
  u.burrowUntil = 0;
  if (!victim) return;
  placeNextTo(b, u, victim);
  emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'leap' });
  dealDamage(b, u, victim, hitDamage(b, u, param(u, k, 'damageFactor')), 'kniff');
  u.targetUid = victim.alive ? victim.uid : null;
  // K36 (Fabi, Phase Kern): Wer unter einem Gegner auftaucht und ihn trifft, zieht ihn an
  provoke(b, u, victim);
}

export function mostHpEnemy(b: Battle, u: Unit): Unit | undefined {
  let best: Unit | undefined;
  for (const o of b.units) {
    if (o.side === u.side || !targetable(b, o) || o.kind === 'tree') continue;
    if (!best || o.hp > best.hp + EPS) best = o;
  }
  return best;
}

/** R27 Stoßtauchen: nach dem Anflug der Stoß; Extraschaden in Prozent vom Leben, wenn er seine Markierung auslöst */
export function updateDive(b: Battle, u: Unit): boolean {
  const d = u.dive;
  if (!d || !u.kniff) return false;
  if (b.t + EPS < d.at) return true; // im Anflug
  u.dive = null;
  const victim = unitById(b, d.targetUid);
  if (!victim || !victim.alive) return false;
  const k = u.kniff;
  const procs = victim.marked && victim.markedBy === u.uid;
  const extra = procs ? victim.maxHp * optParam(u, k, 'maxHpShare') : 0;
  dealDamage(b, u, victim, hitDamage(b, u, param(u, k, 'damageFactor')) + extra, 'kniff');
  emit(b, { t: round(b.t), type: 'kniff', unit: u.uid, kniff: k, target: victim.uid });
  return true;
}

/** F35 Stachelball: hinrollen, dann kreiseln; der Schaden pro Sekunde wächst mit jeder Sekunde (exponentiell) */
export function updateSpin(b: Battle, u: Unit): boolean {
  const sp = u.spin;
  if (!sp || !u.kniff) return false;
  const k = u.kniff;
  // K50 Stachelball (neu): kreiselt an seinem Ort und trifft alle um sich herum
  if (sp.point) {
    if (b.t + EPS >= sp.until) {
      u.spin = null;
      return false;
    }
    if (b.t + EPS >= sp.nextTick) {
      const tick = 0.2;
      sp.nextTick += tick;
      const factor = param(u, k, 'damageFactor') * param(u, k, 'growth') ** (b.t - sp.started) * tick;
      const radius = param(u, k, 'radius');
      for (const o of b.units) {
        if (o.side === u.side || !targetable(b, o) || o.kind === 'tree' || gap(u, o) > radius + EPS) continue;
        dealDamage(b, u, o, hitDamage(b, u, factor), 'kniff');
        if (!u.spin) return true;
      }
    }
    return true;
  }
  let victim = unitById(b, sp.targetUid);
  if (!victim || !targetable(b, victim)) {
    victim = mostHpEnemy(b, u);
    if (!victim) {
      u.spin = null;
      return false;
    }
    sp.targetUid = victim.uid;
  }
  if (sp.started < 0) {
    // Anrollen: deutlich schneller als sein Lauftempo
    if (gap(u, victim) > 0.5 && b.t < u.rushUntil) {
      moveToward(b, u, victim.x, victim.y, param(u, k, 'rushSpeed'));
      return true;
    }
    sp.started = b.t;
    sp.until = b.t + param(u, k, 'duration');
    sp.nextTick = b.t;
  }
  if (b.t + EPS >= sp.until) {
    u.spin = null;
    return false;
  }
  placeNextTo(b, u, victim);
  if (b.t + EPS >= sp.nextTick) {
    // F35 (Fabi): Treffer alle 0,2 s statt 0,25 s, gleicher Schaden pro Sekunde, sieht nur wilder aus
    const tick = 0.2;
    sp.nextTick += tick;
    const elapsed = b.t - sp.started;
    const factor = param(u, k, 'damageFactor') * param(u, k, 'growth') ** elapsed * tick;
    const radius = param(u, k, 'radius');
    for (const o of b.units) {
      if (o.side === u.side || !targetable(b, o)) continue;
      if (o === victim || gap(u, o) <= radius + EPS) dealDamage(b, u, o, hitDamage(b, u, factor), 'kniff');
    }
  }
  return true;
}

/**
 * G18 Windschnitt (Vorschlag Fabi): springt für ein paar Angriffe oder Sekunden zum hintersten Freund,
 * dann zurück in die eigene Hälfte und sucht sich von dort ein neues, meist vorderes Ziel.
 */
export function startRaid(b: Battle, u: Unit): Unit | undefined {
  const back = u.behavior === 'skirmisher' ? secondRowFoe(b, u) : hindmostFoe(b, u);
  if (!back) return undefined;
  return startRaidOn(b, u, back);
}

/** G18, K48: der Abstecher zu diesem Freund (nach der Ansage steht fest, zu wem) */
export function startRaidOn(b: Battle, u: Unit, back: Unit): Unit {
  const raid = getEnemy(u.defId).raid ?? { attacks: 3, seconds: 3 };
  placeNextTo(b, u, back);
  u.raid = { targetUid: back.uid, attacksLeft: raid.attacks, until: b.t + raid.seconds };
  u.raidsStarted += 1;
  u.targetUid = back.uid;
  emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'leap' });
  return back;
}

export function endRaid(b: Battle, u: Unit): void {
  u.raid = null;
  u.targetUid = null;
  u.x = u.homeX;
  u.y = u.homeY;
  emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'return' });
}

/** Läuft gerade ein Abstecher? Dann übernimmt er den Takt. */
export function updateRaid(b: Battle, u: Unit): boolean {
  if (u.behavior !== 'raider' && u.behavior !== 'skirmisher') return false;
  // G18 (Fabi, Phase Kern, 17.10.2026 nachts): nicht gleich im ersten Augenblick springen, das wirkt hektisch
  // K48: Mit Ansagen zeigt erst eine Marke am Freund, auf wen er springt
  if (u.raidsStarted === 0) {
    if (b.t + EPS < (getEnemy(u.defId).raid?.startDelay ?? 0)) return true;
    if (b.setup.telegraphs) return startWindup(b, u, 'sprung', false);
    return !!startRaid(b, u);
  }
  if (!u.raid) return false;
  // S10 (Phase Kern): Ruft eine Leibwache „Komm doch her!“, greift auch ein Windschnitt im Abstecher sie an
  const caller = u.tauntBy !== null && u.tauntAttacks > 0 ? unitById(b, u.tauntBy) : undefined;
  if (caller && targetable(b, caller)) u.raid.targetUid = caller.uid;
  const tgt = unitById(b, u.raid.targetUid);
  if (!tgt || !targetable(b, tgt) || b.t + EPS >= u.raid.until) {
    endRaid(b, u);
    return true;
  }
  u.targetUid = tgt.uid;
  if (gap(u, tgt) > u.range) {
    moveToward(b, u, tgt.x, tgt.y);
    return true;
  }
  if (u.attackCooldown <= EPS && u.raid.attacksLeft > 0) {
    attack(b, u, tgt);
    u.attackCooldown += u.attackInterval;
    u.raid.attacksLeft -= 1;
    // G18 (Fabi, Phase Kern): Nach dem letzten Hieb bleibt er einen Augenblick stehen und springt erst dann zurück
    if (u.raid.attacksLeft <= 0) u.raid.until = Math.min(u.raid.until, b.t + RAID_LINGER);
    else if (u.mut >= u.mutMax - EPS) u.mut = u.mutMax; // Kniff erst nach dem Abstecher
  }
  return true;
}

/** G18 (Fabi, Phase Kern): so lange zwischen letztem Hieb und Rücksprung [ZAHL?] */
export const RAID_LINGER = 0.25;

/**
 * G26 Nieselchen (Fabi, Runde 7 und 8): springt in die zweite Reihe, zu denen, die gleich hinter den Tanks stehen,
 * aber noch vor den Empfindlichsten ganz hinten. Unter denen nimmt es den Schwächsten; gibt es keine zweite Reihe,
 * bleibt es bei der vorderen. [ZAHL?] Reihen-Abstand 6
 */
export const ROW_BAND = 6;
export function secondRowFoe(b: Battle, u: Unit): Unit | undefined {
  const foes = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree');
  if (!foes.length) return undefined;
  const fwd = (o: Unit) => (o.side === 'friend' ? o.x : -o.x);
  const top = Math.max(...foes.map(fwd));
  const bottom = Math.min(...foes.map(fwd));
  const reach = getEnemy(u.defId).raid?.radius ?? 30;
  const behindFront = foes.filter((o) => top - fwd(o) > ROW_BAND && top - fwd(o) <= reach + EPS);
  const middle = behindFront.filter((o) => fwd(o) - bottom > ROW_BAND);
  const pool = middle.length ? middle : behindFront.length ? behindFront : foes.filter((o) => top - fwd(o) <= ROW_BAND);
  return weakestOf(u, pool);
}

/** G18 Windschnitt: das hinterste Ziel der anderen Seite, auch der Baum des Eichhörnchens (Fabi, Phase 3) */
export function hindmostFoe(b: Battle, u: Unit): Unit | undefined {
  let best: Unit | undefined;
  const back = (o: Unit) => (o.side === 'friend' ? o.x : -o.x);
  for (const o of b.units) {
    if (o.side === u.side || !targetable(b, o)) continue;
    if (!best || back(o) < back(best) - EPS) best = o;
  }
  return best;
}
